import { IconButton } from '@/components/atoms/Button';
import { Text } from '@/components/atoms/text';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

interface StyledTabsContainerProps {
  showBackground?: boolean;
}

const shouldForwardProp = (prop: PropertyKey) => !['showBackground'].includes(prop as string);

export const StyledCustomTabsContainer = styled(Box, {
  shouldForwardProp,
})<StyledTabsContainerProps>(({ theme, showBackground }) => ({
  width: '100%',
  margin: '0 auto',
  height: 'auto',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: showBackground ? theme.palette.common.white : 'transparent',
  borderTopLeftRadius: theme.spacing(1),
  borderTopRightRadius: theme.spacing(1),

  [theme.breakpoints.down('sm')]: {
    border: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(2),
  },
}));

interface StyledTabProps {
  active: string;
  tab: string;
}

export const StyledTab = styled(Box)<StyledTabProps>(({ theme, active, tab }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  cursor: 'pointer',
  padding: theme.spacing(0, 2),
  minHeight: '48px',
  color: active === tab.value ? theme.palette.primary.main : theme.palette.text.secondary,
  borderBottom:
    active === tab.value ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
  textTransform: 'none',
  [theme.breakpoints.down('sm')]: {
    flex: '0 0 auto',
    justifyContent: 'normal',
    padding: theme.spacing(0, 1.5),
    borderBottom: 'none',
    backgroundColor: active === tab.value ? theme.palette.primary.main : theme.palette.gray[100],
    borderRadius: theme.spacing(0.5),
    color: active === tab.value ? theme.palette.common.white : theme.palette.text.secondary,
  },
}));

export const StyledTabText = styled(Text)(({ theme }) => ({
  textTransform: 'none',
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
}));

interface StyledPanelProps {
  showBackground?: boolean;
}

export const StyledPanel = styled(Box, { shouldForwardProp })<StyledPanelProps>(
  ({ theme, showBackground }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '100%',
    height: 'calc(100% - 49px)',
    backgroundColor: showBackground ? theme.palette.background.paper : 'transparent',
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderTop: 'none',
    borderBottomLeftRadius: theme.spacing(1),
    borderBottomRightRadius: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      border: `1px solid ${theme.palette.divider}`,
      marginTop: theme.spacing(3),
      borderRadius: theme.spacing(1),
    },
  })
);

export const StyledScrollContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  overflowX: 'auto',
  gap: theme.spacing(1.5),
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  flex: 1,
}));

const BaseScrollButton = styled(IconButton)(() => ({
  position: 'absolute',
  width: 32,
  height: 32,
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'transparent',
    filter: 'brightness(0.9)',
  },
}));

export const StyledScrollButtonLeft = styled(BaseScrollButton)(({ theme }) => ({
  left: -40,
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
  [theme.breakpoints.up('sm')]: {
    display: 'flex',
  },
}));

export const StyledScrollButtonRight = styled(BaseScrollButton)(({ theme }) => ({
  right: -40,
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
  [theme.breakpoints.up('sm')]: {
    display: 'flex',
  },
}));
